// Connection-check launcher (transcripts.md): the agent-scoped empty-state
// action — batch name + version/connection selects + "Run connection check".
// The demo-pack install → batches:preview (validation must be empty) →
// POST /v1/batches pipeline moved verbatim from AgentTranscripts. Vendor
// abstraction: option labels compose display_name + the §2.13 interfaceLabel
// switch (components/agent/lib/connectionConfig) — never duplicated here.
// Container (option queries + mutation) / View (props-pure) split.

import { useMemo, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { defaultBatchDisplayName } from "../../lib/batch";
import { ApiRequestError, isApiRequestError } from "../../lib/errors";
import { createIdempotencyKey } from "../../lib/idempotency";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import {
  Button,
  Inline,
  InlineError,
  Select,
  Stack,
  TextInput,
  useToast,
} from "../../ui";
import { interfaceLabel } from "../agent/lib/connectionConfig";

type BatchCreate = components["schemas"]["BatchCreate"];
type BatchPreviewRequest = components["schemas"]["BatchPreviewRequest"];

export interface ConnectionCheckOption {
  id: string;
  label: string;
}

function resolvedSelection(
  selectedId: string,
  options: readonly ConnectionCheckOption[],
): string {
  if (options.length === 1) {
    return options[0]?.id ?? "";
  }
  return options.some((option) => option.id === selectedId) ? selectedId : "";
}

function launchErrorMessage(error: unknown): string {
  if (isApiRequestError(error)) {
    return error.status.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Connection check failed.";
}

export interface ConnectionCheckLauncherProps {
  agentId: string;
}

export function ConnectionCheckLauncher({
  agentId,
}: ConnectionCheckLauncherProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [displayName, setDisplayName] = useState(() =>
    defaultBatchDisplayName(),
  );
  const [selectedAgentVersionId, setSelectedAgentVersionId] = useState("");
  const [selectedConnectionId, setSelectedConnectionId] = useState("");

  const agentVersions = useListQuery("/v1/agent-versions", {
    params: { agent_id: agentId },
    queryKey: queryKeys.agentVersions.list({ agent_id: agentId }),
  });
  const connections = useListQuery("/v1/connections", {
    params: { agent_id: agentId },
    queryKey: queryKeys.connections.list({ agent_id: agentId }),
  });

  const versionOptions = useMemo(
    () =>
      agentVersions.items
        .filter((item) => item.agent_id === agentId)
        .map((item) => ({ id: item.id, label: item.tag })),
    [agentId, agentVersions.items],
  );
  const connectionOptions = useMemo(
    () =>
      connections.items
        .filter((item) => item.agent_id === agentId)
        .map((item) => ({
          id: item.id,
          label: `${item.display_name} (${interfaceLabel(item)})`,
        })),
    [agentId, connections.items],
  );

  const resolvedAgentVersionId = resolvedSelection(
    selectedAgentVersionId,
    versionOptions,
  );
  const resolvedConnectionId = resolvedSelection(
    selectedConnectionId,
    connectionOptions,
  );

  const runConnectionCheck = useMutation({
    mutationFn: async () => {
      const trimmedDisplayName = displayName.trim();
      if (trimmedDisplayName.length === 0) {
        throw new Error("Name the batch before running a check.");
      }
      const agentVersion = agentVersions.items.find(
        (item) => item.id === resolvedAgentVersionId,
      );
      if (!agentVersion) {
        throw new Error("Select an agent version before running a check.");
      }
      const connection = connections.items.find(
        (item) => item.id === resolvedConnectionId,
      );
      if (!connection) {
        throw new Error("Select a connection before running a check.");
      }

      const installed = await apiClient.POST(
        "/v1/agents/{id}:install-demo-pack",
        {
          body: {
            pack_key: "marker.demo.connection_check",
            pack_version: null,
          },
          params: { path: { id: agentId } },
        },
      );
      if (installed.error) {
        throw new ApiRequestError(installed.error);
      }

      const previewBody: BatchPreviewRequest = {
        agent_version_id: agentVersion.id,
        connection_id: connection.id,
        persona_ids: [installed.data.persona_id],
        sample: null,
        scenario_set_id: installed.data.scenario_set_id,
        simulation_options: {
          batch_concurrency_max: null,
          max_duration_seconds: 60,
        },
        template_id: null,
        sweep: {
          accent: [],
          background: [],
          language: [],
          variables: [],
        },
      };
      const preview = await apiClient.POST("/v1/batches:preview", {
        body: previewBody,
      });
      if (preview.error) {
        throw new ApiRequestError(preview.error);
      }
      if ((preview.data.validation_groups ?? []).length > 0) {
        throw new Error("Connection check demo pack did not validate.");
      }

      const batchBody: BatchCreate = {
        ...previewBody,
        display_name: trimmedDisplayName,
      };
      const batch = await apiClient.POST("/v1/batches", {
        body: batchBody,
        params: {
          header: { "Idempotency-Key": createIdempotencyKey() },
        },
      });
      if (batch.error) {
        throw new ApiRequestError(batch.error);
      }
      return batch.data;
    },
    onError: (error: unknown) => {
      toast.error(launchErrorMessage(error));
    },
    onSuccess: async () => {
      toast.success("Connection check launched.");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.batches.list(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.transcripts.all,
        }),
      ]);
    },
  });

  return (
    <ConnectionCheckLauncherView
      connectionOptions={connectionOptions}
      displayName={displayName}
      // UI-LAW §4a: a failed launch is a blocking error — the toast is never
      // its sole surface; the same formatted message renders inline below.
      errorMessage={
        runConnectionCheck.isError
          ? launchErrorMessage(runConnectionCheck.error)
          : undefined
      }
      isRunning={runConnectionCheck.isPending}
      selectedAgentVersionId={resolvedAgentVersionId}
      selectedConnectionId={resolvedConnectionId}
      versionOptions={versionOptions}
      onDisplayNameChange={setDisplayName}
      onRun={() => {
        runConnectionCheck.mutate();
      }}
      onSelectedAgentVersionChange={setSelectedAgentVersionId}
      onSelectedConnectionChange={setSelectedConnectionId}
    />
  );
}

export interface ConnectionCheckLauncherViewProps {
  connectionOptions: ConnectionCheckOption[];
  displayName: string;
  // Formatted message of a failed launch, rendered as an InlineError beside
  // the launcher controls (the container also toasts it).
  errorMessage?: string;
  isRunning: boolean;
  selectedAgentVersionId: string;
  selectedConnectionId: string;
  versionOptions: ConnectionCheckOption[];
  onDisplayNameChange: (displayName: string) => void;
  onRun: () => void;
  onSelectedAgentVersionChange: (id: string) => void;
  onSelectedConnectionChange: (id: string) => void;
}

export function ConnectionCheckLauncherView({
  connectionOptions,
  displayName,
  errorMessage,
  isRunning,
  selectedAgentVersionId,
  selectedConnectionId,
  versionOptions,
  onDisplayNameChange,
  onRun,
  onSelectedAgentVersionChange,
  onSelectedConnectionChange,
}: ConnectionCheckLauncherViewProps) {
  const canRun =
    selectedAgentVersionId.length > 0 &&
    selectedConnectionId.length > 0 &&
    displayName.trim().length > 0;

  return (
    <Stack gap={3}>
      <TextInput
        label="Batch name"
        required
        value={displayName}
        onChange={(event) => {
          onDisplayNameChange(event.currentTarget.value);
        }}
      />
      <Inline align="end" gap={3}>
        <Select
          label="Version"
          size="sm"
          value={selectedAgentVersionId}
          onChange={(event) => {
            onSelectedAgentVersionChange(event.currentTarget.value);
          }}
        >
          <option value="">Select version</option>
          {versionOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
        <Select
          label="Connection"
          size="sm"
          value={selectedConnectionId}
          onChange={(event) => {
            onSelectedConnectionChange(event.currentTarget.value);
          }}
        >
          <option value="">Select connection</option>
          {connectionOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
      </Inline>
      {errorMessage !== undefined ? (
        <InlineError>{errorMessage}</InlineError>
      ) : null}
      <Button
        disabled={isRunning || !canRun}
        variant="secondary"
        onClick={onRun}
      >
        {isRunning ? "Launching..." : "Run connection check"}
      </Button>
    </Stack>
  );
}
